import { UserQuoteHeader } from '@modules/oracle/components/UserQuote/UserQuoteHeader';
import { UserQuoteLoader } from '@modules/oracle/components/UserQuote/UserQuoteLoader';
import { UserQuoteText } from '@modules/oracle/components/UserQuote/UserQuoteText';
import { useGetSummaryUserQuote } from '@modules/oracle/hooks/useGetSummaryUserQuote';
import {
  UserQuotesByExampleID,
  UserQuoteTokensByResponseID,
} from '@modules/oracle/types';
import { Divider, Stack } from '@mui/material';
import { FeedbackSummaryExample } from 'generated/graphql';
import { useEffect } from 'react';

interface UserQuoteProps {
  sessionID: string;
  recordID: string;
  labelID?: string;
  feedbackSummaryExample: FeedbackSummaryExample;
  userQuoteTokensByResponseID: UserQuoteTokensByResponseID;
  userQuotesByExampleID: UserQuotesByExampleID;
}

export function UserQuote({
  feedbackSummaryExample,
  labelID,
  sessionID,
  recordID,
  userQuotesByExampleID,
  userQuoteTokensByResponseID,
}: UserQuoteProps) {
  const [getSummaryUserQuote, { summaryUserQuote, isLoading }] =
    useGetSummaryUserQuote();
  const userQuoteFromExample =
    userQuotesByExampleID[feedbackSummaryExample.id]?.[recordID];

  const shouldShowLoader = userQuoteFromExample
    ? false
    : isLoading || !summaryUserQuote;

  useEffect(() => {
    if (userQuoteFromExample || !recordID || !labelID) {
      return;
    }

    getSummaryUserQuote({
      labelID,
      recordID,
      sessionID,
      claim: feedbackSummaryExample.claimExample?.forClaim,
    });
  }, [
    sessionID,
    feedbackSummaryExample.claimExample?.forClaim,
    labelID,
    getSummaryUserQuote,
    recordID,
    userQuoteFromExample,
  ]);

  if (shouldShowLoader) {
    return <UserQuoteLoader />;
  }

  const userQuoteTokens = userQuoteFromExample
    ? [{ content: userQuoteFromExample.quote }]
    : summaryUserQuote?.responseID
    ? userQuoteTokensByResponseID[summaryUserQuote.responseID]
    : null;

  if (!userQuoteTokens) {
    return <UserQuoteLoader />;
  }

  return (
    <Stack spacing={1.5} sx={{ py: 1.5, px: 2.5 }}>
      <UserQuoteHeader
        recordID={recordID}
        summaryUserQuote={userQuoteFromExample ?? summaryUserQuote}
      />
      <Divider />
      <UserQuoteText userQuoteTokens={userQuoteTokens} />
    </Stack>
  );
}
